import { Component, Renderer2, Injector, OnInit, OnDestroy, inject } from "@angular/core";
import { ActivatedRoute, NavigationCancel, NavigationEnd, NavigationStart, Router } from "@angular/router";
import { select, Store } from "@ngrx/store";
import { combineLatest, Subject } from 'rxjs';
import camelCase from "lodash/camelCase";
import { AppState } from "./@store/reducers";
import { StsSelectors } from "./@store/selectors/sts.selectors";
import { ClaimTypesEnum } from "src/app/plasma-ui-common/enums/claim-types.enum";
import { EBodyClass } from "src/app/plasma-ui-common/enums/body-class.enum";
import { ResultsViewerAllowedRoutes } from "src/app/plasma-ui-common/constants/results.constants";
import { ThemeNames } from "src/app/plasma-ui-common/enums/theme-names.enum";
import { UserTypes } from "src/app/plasma-ui-common/enums/user-type.enum";
import { AuthService } from "./service/auth/auth.service";
import { Idle, DEFAULT_INTERRUPTSOURCES, AutoResume } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { HeaderActions } from 'src/app/@store/actions/header.actions';
import { HeaderSelectors } from 'src/app/@store/selectors/header.selectors';
import { filter,take, tap, withLatestFrom, map, distinctUntilChanged, takeUntil } from "rxjs/operators";
import { getRedirectUrl, isPrivateRoute, trimUrl } from "./plasma-ui-common/utils/common.utils";
import { AuthGuard } from "./service/authGuard/auth.guard";
import { LoggerService } from "./service/logger.service";
import { LocalStorageKeys } from "./plasma-ui-common/constants/storage-keys.constants";
import { ConfigService } from "./service/config/config.service";
import { TranslocoService } from "@jsverse/transloco";
import { parseJwt } from "./plasma-ui-common/utils/parse-jwt";
import { UserInfo, UserType } from "./plasma-ui-common/services/user-role.service";
import { Claim } from "./plasma-ui-common/synlab-access-ui-components/core";
import { MPI_ID_QUERY_PARAM_NAME } from "./plasma-ui-common/sa-ui-library/projects/sa-ui-library/src/constants";
import { MpiIdContextService } from "./service/mpi-id/mpi-id-context.service";
import { ExternalReturnUrlService } from "./service/external-return-url.service";

declare const SALiveChat: any;

@Component({
    selector: "app-root",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],
    providers: [],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {

  public runShimmerLoader: boolean = true;

  /** What the route announcer is currently saying; read by the template. */
  public routeAnnouncement: string = '';

  private prevurl: string;
  public readonly isEETenant$ = this.store.pipe(select(StsSelectors.isEETenant));
  public readonly isFITenant$ = this.store.pipe(select(StsSelectors.isFITenant));
  public readonly loadingBar$ = this.store.pipe(select(HeaderSelectors.loadingBarShown));
  public readonly useNewNav$ = this.store.pipe(select(StsSelectors.useNewNav));

  public themeName: string;
  public themeNames = ThemeNames;

  private renderer: Renderer2;
  private idle: Idle;
  private keepalive: Keepalive;
  private logr: LoggerService;
  private idleSubscription: any;
  private timeoutWarningSubscription: any;
  private idleStartSubscription: any;

  private readonly onDestroy$ = new Subject<void>();

  public userName: string = '';

  public logoRedirectUrl: string = '';

  public activatedRoute = inject(ActivatedRoute);

  public readonly wards$ = this.store.select(HeaderSelectors.getWards);

  private readonly isUkDfp$ = this.store.select(StsSelectors.isDFPTenant);

  public readonly logo$ = this.isUkDfp$.pipe(map(isUkDfp => isUkDfp ? '/assets/img/ukdfplogo.png' : '/assets/img/synlab-logo.svg'));

  public currentLangCode: string = '';

  public currentUserClaims: Claim[] = [];

  private readonly translocoService = inject(TranslocoService);


  public selectedMpiId: string = '';

  private readonly mpiIdContext = inject(MpiIdContextService);
  private readonly externalReturnUrl = inject(ExternalReturnUrlService);

  constructor(
    private config: ConfigService,
    private store: Store<AppState>,
    private router: Router,
    private auth: AuthService,
    private injector: Injector,
  ) {
    // Before anything can trigger the sign-in redirect: the ward being viewed has to be
    // picked up from the opening URL and then re-attached to every later navigation.
    this.mpiIdContext.init();

    // Same window: the referrer names the app the user came from only until the sign-in
    // redirect replaces it with the STS.
    this.externalReturnUrl.capture();

    const { pathname } = window.location;
    if (pathname.startsWith('/results/shared-result/')) {
      localStorage.setItem(LocalStorageKeys.REDIRECT_URL, window.location.pathname + window.location.search);
    }

    this.store.dispatch(HeaderActions.startLoadingBar());

    this.renderer = this.injector.get<Renderer2>(Renderer2);
    this.idle = this.injector.get<Idle>(Idle);
    this.keepalive = this.injector.get<Keepalive>(Keepalive);
    this.logr = this.injector.get<LoggerService>(LoggerService);

    this._fetchFreshToken();
    this.seTitle();
    this.syncDocumentLang();
    this.announceRouteChanges();

    this.router.events.pipe(
      withLatestFrom(this.store.select(StsSelectors.getWhiteLabelName))).subscribe(([event, whiteLabelName]) => {
      if (event instanceof NavigationStart) {
        if (this.prevurl) { this.renderer.removeClass(document.body, this.prevurl); }
        const page = camelCase(event.url.slice(1) || "");
        if (page) this.renderer.addClass(document.body, page);
        this.prevurl = page;
        this.store.dispatch(HeaderActions.closeMenu());
      } else if (event instanceof NavigationEnd || event instanceof NavigationCancel) {
        // The shimmer stands in for the app while it boots, so it comes down as soon as any
        // navigation settles. It used to come down only for URLs containing "results", which
        // left every other landing — a direct load or refresh of /access-verification, /home,
        // /find-patient — showing it forever, and with it the `appIsLoading` class the template
        // puts on the content wrapper: `display: none !important`. The page rendered correctly
        // into a DOM nobody could see.
        this.runShimmerLoader = false;

        if (event instanceof NavigationEnd && event.url.includes('/results/shared-result/')) {
          localStorage.removeItem(LocalStorageKeys.REDIRECT_URL);
        }

        if (whiteLabelName) { this.renderer.addClass(document.body, `tenant-${whiteLabelName}`); }
        setTimeout(() => this.store.dispatch(HeaderActions.endLoadingBar()));
      }
    });

    this.config.envConfig$.pipe(
      filter(v=>!!v), take(1),
      tap((config) => {
        const enableIdleTime = String(config.EnableIdleTimeOut).toLowerCase() === 'true';
        if (enableIdleTime) {
          const idleTimeTemp = Number(config.IdleTimeOutInSeconds) || 900;
          const idleTime = Number(idleTimeTemp);

          this.logr.log({ idleTime });
          this._enableIdleTimer(idleTime);
        }
      })).subscribe();
  }

  private subscribeToUserClaims() {
    this.store.pipe(select(StsSelectors.getClaims)).subscribe(claims => {
      if (claims) {
        const userTypes = claims.filter(claim => claim.claimType === 'user_type').map(claim => claim.claimValue);
        if (
          userTypes.includes(UserType.ResultsViewer)
          && !ResultsViewerAllowedRoutes.some(route => this.router.url.startsWith(route))
          && this.router.url !== '/'
        ) {
          console.log('Test url: ', this.router.url);
          console.log('Test href: ', window.location.href);
          window.location.href = this.config._envConfig?.baseProfileUrl + '/unauthorized';
        }
      }
    });
  }

  ngOnInit(): void {
    this.subscribeToUserClaims();

    this.handleTwoWayMessagingSetup();

    this.themeName = this.config._envConfig?.ThemeName;
    this.logoRedirectUrl = this.config._envConfig?.LogoRedirectUrl;
    this.trackCurrentLanguage();
    this.subscribeToMpiId();
    this.subscribeToUserInfo();
  }

  private trackCurrentLanguage() {
    this.currentLangCode = this.translocoService.getActiveLang();
    this.translocoService.langChanges$.pipe(takeUntil(this.onDestroy$)).subscribe((value) => {
      this.currentLangCode = value;
    });
  }

  private subscribeToUserInformation() {
    this.store
      .select(StsSelectors.getUserBasicInfo)
      .pipe(take(1))
      .subscribe(({ givenName, familyName, synlabId }) => {
        if (!givenName || !familyName || !synlabId) return;

        this.userName = `${givenName} ${familyName[0]}.`;
      });
  }

  private subscribeToUserInfo() {
    this.store
      .select(StsSelectors.getUserBasicInfo)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(({ synlabId, claims }) => {
        this.currentUserClaims = claims;

        if (!synlabId) return;
        this.store.dispatch(HeaderActions.getRelatedUsers({ synlabId: synlabId }));
      });
    this.wards$.pipe(takeUntil(this.onDestroy$)).subscribe(wards => {
        const wardMpiId = this.activatedRoute.snapshot.queryParamMap.get(MPI_ID_QUERY_PARAM_NAME);
        if (wardMpiId) {
          const selectedWard = wards.find(ward => ward.mpiId === wardMpiId);
          this.userName = `${selectedWard.firstName} ${selectedWard.lastName[0]}.`;
        }
    });
  }

  private subscribeToMpiId() {
    combineLatest([
      this.store.select(StsSelectors.getClaims),
      this.activatedRoute.queryParamMap,
      this.wards$,
    ]).pipe(takeUntil(this.onDestroy$))
      .subscribe(([claims, params, wards]) => {
        const wardMpiId = params.get(MPI_ID_QUERY_PARAM_NAME);
        const mpiId = claims.find(claim => claim.claimType === ClaimTypesEnum.MPI_ID)?.claimValue;
        if (wardMpiId) {
          const selectedWard = (wards || []).find(ward => ward.mpiId === wardMpiId);
          if (selectedWard) {
            this.userName = `${selectedWard.firstName} ${selectedWard.lastName[0]}.`;
          }
          this.selectedMpiId = wardMpiId;
        } else if (mpiId) {
          this.selectedMpiId = mpiId;
          this.subscribeToUserInformation();
        } else {
          this.subscribeToUserInformation();
        }
      });
  }

  private handleTwoWayMessagingSetup() {
    this.auth._manager.events.addUserLoaded(user => {
      SALiveChat.updateToken(user.access_token);
    });

    this.auth.userInfo$.pipe(distinctUntilChanged((prev, curr) => !!prev === !!curr)).subscribe(userInfo => {
      const enableSynchat =
          this.config._envConfig.EnableSynchat.toLowerCase() === 'true';
        if (!enableSynchat) return;

      this.initLiveChat(userInfo);
    });
  }

  private loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = true;

      script.onload = () => resolve();
      script.onerror = () => reject(`Failed to load script: ${src}`);

      document.body.appendChild(script);
    });
  }

  private async initLiveChat(userInfo: UserInfo) {
    if (
      userInfo &&
      (userInfo.user_type === UserTypes.SYNLAB_ACCESS_USER ||
        userInfo.user_type.includes(UserTypes.SYNLAB_ACCESS_USER))
    ) {
      const oidcKey = Object.keys(sessionStorage).find(key => key.includes('oidc.user:'));
      const token = JSON.parse(sessionStorage.getItem(oidcKey));
      const { family_name: familyName, given_name: givenName } = userInfo;
      const { synlab_id: synlabId } = parseJwt(token.access_token);
      const { BaseSynchatUrl } = this.config._envConfig;

      // Inject script here
      await this.loadScript(`${BaseSynchatUrl}/embed-livechat.js`);

      SALiveChat.initialize({
        serverUrl: BaseSynchatUrl,
        token: token.access_token,
        userName: `${givenName} ${familyName}`,
        synlabId,
      });
    } else {
      SALiveChat.remove();
    }
  }

  reset() {
    // we'll call this method when we want to start/reset the idle process
    // reset any component state and be sure to call idle.watch()
    this.idle.watch();
  }

  /**
   * Sends focus into the routed content, past the header.
   *
   * `<main>` carries `tabindex="-1"` purely so it can receive this; it stays out of the
   * tab order.
   */
  public skipToContent(event: Event): void {
    event.preventDefault();

    const main = document.getElementById('main-content');
    main?.focus();
    main?.scrollIntoView();
  }

  /**
   * Announces each new page once its title has been set.
   *
   * Deferred by a tick because PageTitleStrategy sets the title on the same NavigationEnd
   * this listens to, and cleared afterwards so returning to the same route announces again
   * rather than being ignored as unchanged text.
   */
  private announceRouteChanges(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.onDestroy$),
      )
      .subscribe(() => {
        setTimeout(() => {
          this.routeAnnouncement = document.title;
          setTimeout(() => (this.routeAnnouncement = ''), 1000);
        });
      });
  }

  logout() {
    this.auth.startSignoutMainWindow()
  }

  /**
   * Keeps the document language in step with the language actually on screen.
   *
   * index.html hardcodes lang="en" and nothing ever changed it, while the app serves other
   * languages - Blue Pulse is Italian - so assistive technology read every page with English
   * pronunciation rules (WCAG 3.1.1). Transloco is the source of truth for what is rendered,
   * and langChanges emits the active language on subscribe as well as on every later switch.
   */
  private syncDocumentLang(): void {
    this.translocoService.langChanges$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(lang => this.renderer.setAttribute(document.documentElement, "lang", lang));
  }

  seTitle() {
    combineLatest([
      this.store.pipe(select(StsSelectors.isEETenant)),
    ]).pipe(
      take(1),
      tap(() => {
        // The document title used to be set here as well, once for the whole session.
        // It is per-route now (PageTitleStrategy); setting it here too would race that.
        this.renderer.addClass(document.body, EBodyClass.NORMAL);
      })
    ).subscribe()
  }

  private _fetchFreshToken() {
    const referrer = trimUrl(window.document?.referrer);
    const origin = window.location?.host;

    this.logr.log({ routes: this.router.config });
    this.logr.log({ referrer, origin });
    if (referrer != origin) {
      const redirect = getRedirectUrl();
      const routes = this.router.config;
      const privateRoute = isPrivateRoute(redirect, routes, AuthGuard);
      this.logr.log(
        "Check if redirectURL is private route before calling startSigninMainWindow():\n",
        { privateRoute, redirect }
      );

      if (privateRoute) {
        document.querySelector('body').classList.add('signin-redirecting');
        this.auth.signinRedirectTrigger$.next(Date.now());
        if (redirect) {
          localStorage.setItem(LocalStorageKeys.REDIRECT_URL, redirect);
        }
      }
    }
  }

  private _enableIdleTimer(idleTimerInSeconds: number) {
    this.idle.setIdle(30); // how long can they be inactive before considered idle, in seconds
    this.idle.setTimeout(idleTimerInSeconds); // how long can they be idle before considered timed out, in seconds
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    this.idle.setAutoResume(AutoResume.idle);

    // Clean up previous subscriptions if they exist
    if (this.idleSubscription) {
      this.idleSubscription.unsubscribe();
    }
    if (this.timeoutWarningSubscription) {
      this.timeoutWarningSubscription.unsubscribe();
    }
    if (this.idleStartSubscription) {
      this.idleStartSubscription.unsubscribe();
    }

    this.idleSubscription = this.idle.onTimeout.subscribe(() => {
      this.logr.log({ onTimeout: true });
      this.auth.isLoggedInObs().pipe(
        take(1), tap(isLoggedIn => {
          this.logr.log({ isLoggedIn });
          if (isLoggedIn) {
            this.auth.startSignoutMainWindow();
          }
        })
      ).subscribe();
    });

    this.idleStartSubscription = this.idle.onIdleStart.subscribe(() => {
      this.logr.log("You've gone idle!");
      // Add UI notification for idle state here if needed
    });

    this.timeoutWarningSubscription = this.idle.onTimeoutWarning.subscribe((countdown) => {
      this.logr.log("You will time out in " + countdown + " seconds!");
      // Add UI notification for timeout warning here if needed
    });

    this.keepalive.interval(300); // 5 minutes
    // Keepalive should ping server to maintain session, not log idle status
    this.keepalive.onPing.subscribe(() => {
      // Ping the server to maintain session, don't log idle status
      // The actual idle detection should be handled by the Idle service
    });

    // right when the component initializes, start reset state and start watching
    this.reset();
  }

  ngOnDestroy(): void {
    // Clean up idle subscriptions
    if (this.idleSubscription) {
      this.idleSubscription.unsubscribe();
    }
    if (this.timeoutWarningSubscription) {
      this.timeoutWarningSubscription.unsubscribe();
    }
    if (this.idleStartSubscription) {
      this.idleStartSubscription.unsubscribe();
    }

    // Clean up any remaining timers
    this.idle.stop();
    this.onDestroy$.next();
  }
}
